import { graphqlFetch } from "@/shared/graphql/graphqlFetch";
import { GET_USERS, GET_USER } from "@/shared/graphql/queries";
import { GRAPHQL_QUERY_LIMIT } from "@/shared/config/constants";
import type { User } from "../types";

/**
 * User service
 *
 * Business logic for users.
 * Uses GraphQL for queries.
 */

export interface CreateUserDto {
  firstName: string;
  lastName: string;
  email?: string;
  role: string;
  policyId?: string;
  permissionIds?: string[];
  warehouseIds?: string[];
  personId?: string;
  /**
   * Optional password. **Ignored for ApiUser** — external integrations
   * authenticate via API keys issued from the user's edit dialog
   * (see ticket #33), not a password.
   */
  password?: string;
  /** TechnicalUser & TechnicalManager: service team memberships (≥1 required). */
  queueIds?: string[];
  /** Optional default-filter Service Team for TechnicalUser/Manager; must be in queueIds. */
  primaryQueueId?: string | null;
  /** @deprecated Tech Manager scope is now via queueIds; kept only for legacy callers. */
  locationCategoryIds?: string[];
  /**
   * Always an array. User / LocationUser: exactly one id (single-select UI).
   * TechnicalManager: zero or more (combined with locationCategoryIds must be ≥1).
   */
  locationIds?: string[];
}

interface PaginationInput {
  page?: number;
  limit?: number;
}

interface GraphQLUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  emailVerified?: boolean;
  role: string;
  isActive: boolean;
  accountId: string | null;
  account?: {
    id: string;
    name: string;
  } | null;
  contactId: string | null;
  avatarFileKey: string | null;
  warehouseIds?: string[] | null;
  queueIds?: string[] | null;
  primaryQueueId?: string | null;
  locationCategoryIds?: string[] | null;
  locationIds?: string[] | null;
  queues?: { id: string; name: string }[] | null;
  locationCategories?: { id: string; name: string; color?: string | null }[] | null;
  assignedLocations?: { id: string; name: string }[] | null;
  accessibleLocations?: { id: string; name: string }[] | null;
  warehouses?: { id: string; name?: string | null }[] | null;
  createdAt: string;
  updatedAt: string;
}

interface GetUsersResponse {
  users: {
    data: GraphQLUser[];
    meta: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}

interface GetUserResponse {
  user: GraphQLUser | null;
}

function mapGraphQLUserToUser(gqlUser: GraphQLUser): User {
  return {
    id: gqlUser.id,
    firstName: gqlUser.firstName ?? "",
    lastName: gqlUser.lastName ?? "",
    email: gqlUser.email || "",
    role: gqlUser.role,
    isActive: gqlUser.isActive,
    emailVerified: gqlUser.emailVerified,
    warehouseIds: gqlUser.warehouseIds ?? undefined,
    queueIds: gqlUser.queueIds ?? undefined,
    primaryQueueId: gqlUser.primaryQueueId ?? null,
    locationCategoryIds: gqlUser.locationCategoryIds ?? undefined,
    locationIds: gqlUser.locationIds ?? undefined,
    queues: gqlUser.queues ?? undefined,
    locationCategories: gqlUser.locationCategories ?? undefined,
    assignedLocations: gqlUser.assignedLocations ?? undefined,
    accessibleLocations: gqlUser.accessibleLocations ?? undefined,
    warehouses: gqlUser.warehouses ?? undefined,
    account: gqlUser.account
      ? {
          id: gqlUser.account.id,
          name: gqlUser.account.name,
        }
      : undefined,
    tenant_id: gqlUser.accountId || "",
    createdAt: gqlUser.createdAt ?? new Date(0).toISOString(),
    updatedAt: gqlUser.updatedAt ?? new Date(0).toISOString(),
  };
}

export const userApi = {
  /**
   * Get a single user by ID
   */
  async getUser(id: string): Promise<User> {
    const data = await graphqlFetch<GetUserResponse>(GET_USER, { id });

    if (!data.user) {
      throw new Error("User not found");
    }

    return mapGraphQLUserToUser(data.user);
  },
  /**
   * Get users with optional pagination and sort. Returns paginated result when pagination is provided.
   */
  async getUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortDirection?: "asc" | "desc";
    /** Filter to users with these roles only (e.g. TechnicalUser, TechnicalManager for ticket queues) */
    roleIn?: string[];
    /** Filter by active status; omit to return both active and inactive (e.g. user management table) */
    isActive?: boolean;
  }): Promise<{ data: User[]; meta: GetUsersResponse["users"]["meta"] }> {
    const pagination: PaginationInput | undefined = params
      ? {
          page: params.page ?? 1,
          limit: params.limit ?? GRAPHQL_QUERY_LIMIT,
        }
      : undefined;

    const userSortByGraphQL: Record<string, string> = {
      firstName: "FIRST_NAME",
      lastName: "LAST_NAME",
      role: "ROLE",
      location: "LOCATION_NAME",
      createdAt: "CREATED_AT",
      updatedAt: "UPDATED_AT",
    };
    const sortByGraphQL = params?.sortBy && (userSortByGraphQL[params.sortBy] ?? "CREATED_AT");
    const sortDirectionGraphQL = params?.sortDirection === "desc" ? "DESC" : "ASC";
    const sort =
      params?.sortBy && params?.sortDirection
        ? { sortBy: sortByGraphQL, sortDirection: sortDirectionGraphQL }
        : null;

    const filter = {
      ...(params?.roleIn?.length && { roleIn: params.roleIn }),
      ...(params?.isActive !== undefined && { isActive: params.isActive }),
    };

    const data = await graphqlFetch<GetUsersResponse>(GET_USERS, { pagination, sort, filter });

    return {
      data: data.users.data.map(mapGraphQLUserToUser),
      meta: data.users.meta,
    };
  },
  /**
   * Create a new user
   * Only Account Admins can create users
   * Note: This still uses REST API as mutations are not implemented in GraphQL
   */
  async createUser(dto: CreateUserDto): Promise<User> {
    const { api } = await import("@/shared/api");
    const { API_ENDPOINTS } = await import("@/shared/api");
    const { data } = await api.post<User>(API_ENDPOINTS.USERS.create(), dto);
    return data;
  },
  /**
   * Update an existing user
   * Only Account Admins can update users
   */
  async updateUser(
    id: string,
    dto: Partial<CreateUserDto> & {
      isActive?: boolean;
      email?: string;
      warehouseIds?: string[];
      queueIds?: string[];
      primaryQueueId?: string | null;
      locationCategoryIds?: string[];
      locationIds?: string[];
    }
  ): Promise<User> {
    const { api } = await import("@/shared/api");
    const { API_ENDPOINTS } = await import("@/shared/api");
    const { data } = await api.patch<User>(API_ENDPOINTS.USERS.update(id), dto);
    return data;
  },
  /**
   * Get magic link for a Location User
   */
  async getMagicLink(userId: string): Promise<string | null> {
    const { api } = await import("@/shared/api");
    const { API_ENDPOINTS } = await import("@/shared/api");
    const response = await api.get<{ magicLinkUrl: string | null }>(
      API_ENDPOINTS.USERS.magicLink(userId)
    );
    return response.data.magicLinkUrl;
  },
  /**
   * Regenerate magic link for a Location User
   */
  async regenerateMagicLink(userId: string): Promise<string> {
    const { api } = await import("@/shared/api");
    const { API_ENDPOINTS } = await import("@/shared/api");
    const response = await api.post<{ magicLinkUrl: string }>(
      API_ENDPOINTS.USERS.regenerateMagicLink(userId)
    );
    return response.data.magicLinkUrl;
  },
  /**
   * Resend invitation email for a user whose email is not yet verified.
   */
  async resendInvitation(userId: string): Promise<{ message: string }> {
    const { api } = await import("@/shared/api");
    const { API_ENDPOINTS } = await import("@/shared/api");
    const { data } = await api.post<{ message: string }>(
      API_ENDPOINTS.USERS.resendInvitation(userId),
      {}
    );
    return data;
  },
  /**
   * Delete (soft delete) a user
   * Only Account Admins can delete users
   */
  async deleteUser(id: string): Promise<void> {
    const { api } = await import("@/shared/api");
    const { API_ENDPOINTS } = await import("@/shared/api");
    await api.delete(API_ENDPOINTS.USERS.delete(id));
  },
  /**
   * Update current user's preferred language (persisted for next login and for emails).
   */
  async updatePreferredLanguage(preferredLanguage: string): Promise<void> {
    const { api } = await import("@/shared/api");
    const { API_ENDPOINTS } = await import("@/shared/api");
    await api.patch<{ preferredLanguage: string | null }>(API_ENDPOINTS.USERS.preferredLanguage(), {
      preferredLanguage,
    });
  },
};
